import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { BsCalendar2Date } from "react-icons/bs";

/**
 * Field mapping.
 * The sample for view 1879 is a booking-file record and has none of the report
 * columns, so each column lists the API field names to try (first hit wins).
 * Adjust these to the real column names returned by your view.
 */

const FIELDS = {
  fileDate: ["FILEDATE"],
  fileNo: ["FILENO"],
  client: ["CLIENT"],
  bookingNo: ["BOOKINGNO"],
  portOfLoading: ["PORTOFLOADING"],
  LoadingDate: ["LOADINGDATE"],
  Consignee: ["CONSIGNEE"],
};


const getField = (item, keys) => {
  for (const k of keys) {
    if (item[k] !== undefined && item[k] !== null && item[k] !== "") {
      return item[k];
    }
  }
  return "";
};

// Dates from the API are UTC-midnight ISO strings; read them as UTC so the
// calendar day never shifts with the browser's timezone.
const toYMD = (value) => {
  if (!value) return "";
  // View format: "2026-02-06T00:00:00.000Z" -> take the date part directly
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  // Fallback for DD/MM/YYYY
  const m = moment(str, "DD/MM/YYYY", true);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const displayDate = (ymd) => (ymd ? moment(ymd, "YYYY-MM-DD").format("DD/MM/YYYY") : "");

const formatVgm = (v) => {
  const n = Number(v);
  if (v === "" || Number.isNaN(n)) return "";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const DATE_FMT = "YYYY-MM-DD";

const getPresetRange = (filter) => {
  const today = moment();
  switch (filter) {
    case "last3days":
      return [today.clone().subtract(3, "days"), today];
    case "last7days":
      return [today.clone().subtract(7, "days"), today];
    case "last30days":
      return [today.clone().subtract(30, "days"), today];
    case "lastMonth": {
      const m = today.clone().subtract(1, "month");
      return [m.clone().startOf("month"), m.clone().endOf("month")];
    }
    case "lastQuarter": {
      const q = today.clone().subtract(1, "quarter");
      return [q.clone().startOf("quarter"), q.clone().endOf("quarter")];
    }
    case "lastYear": {
      const y = today.clone().subtract(1, "year");
      return [y.clone().startOf("year"), y.clone().endOf("year")];
    }
    default:
      return null;
  }
};

const CfsBooking = () => {
  const BASEURL_GLOBAL = process.env.REACT_APP_BASEURL_GLOBAL;
  const authToken = window.sessionStorage.getItem("auth-token");
  const sessiontoken = window.sessionStorage.getItem("session-token");

  // Dates are kept as plain "YYYY-MM-DD" strings (no timezone conversion).
  const [fromdate, setFromdate] = useState(
    moment().startOf("month").format(DATE_FMT)
  );
  const [todate, setTodate] = useState(moment().format(DATE_FMT));
  const [activeDates, setActiveDates] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");


  const [searchText, setSearchText] = useState("");
  const terms = searchText.toLowerCase().split(/\s+/).filter(Boolean);



  const filterRef = useRef(null);

  // Close the date popup when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveDates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load the full view once. Dates are NOT sent to the API; they only filter
  // the loaded data on the client (see `groups` below).
  useEffect(() => {
    let cancelled = false;

    const fetchAnalysis = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${BASEURL_GLOBAL}/globalViewHandler?viewname=1879`,
          {
            headers: {
              "auth-token": authToken,
              "session-token": sessiontoken,
            },
          }
        );
        if (!cancelled) {
          setAnalyticsData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Error fetching analysis:", err);
        if (!cancelled) {
          setError("Failed to load data. Please try again.");
          setAnalyticsData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAnalysis();
    return () => {
      cancelled = true;
    };
  }, [BASEURL_GLOBAL, authToken, sessiontoken]);

  const invalidRange = !!fromdate && !!todate && moment(fromdate).isAfter(moment(todate));

  const handleDateFilterChange = useCallback((filter) => {
    const range = getPresetRange(filter);
    if (!range) return;
    setFromdate(range[0].format(DATE_FMT));
    setTodate(range[1].format(DATE_FMT));
    setActiveDates(false);
  }, []);

  // Group rows by gate-out date, filtered to the selected range
  const groups = useMemo(() => {
    const map = new Map();
    if (!fromdate || !todate || fromdate > todate) return [];

    analyticsData.forEach((item) => {
      const gateOut = toYMD(getField(item, FIELDS.LoadingDate));
      if (!gateOut || gateOut < fromdate || gateOut > todate) return;

      const row = {
        fileDate: toYMD(getField(item, FIELDS.fileDate)),
        fileNo: getField(item, FIELDS.fileNo),
        client: getField(item, FIELDS.client),
        bookingNo: getField(item, FIELDS.bookingNo),
        portOfLoading: getField(item, FIELDS.portOfLoading),
        gateOut,
        Consignee: getField(item, FIELDS.Consignee),
      };

      if (terms.length > 0) {
  const haystack = [
    displayDate(row.fileDate),
    row.fileNo,
    row.client,
    row.bookingNo,
    row.portOfLoading,
    displayDate(row.gateOut),
    formatVgm(row.Consignee),
    row.Consignee,
  ]
    .join(" ")
    .toLowerCase();
  if (!terms.every((t) => haystack.includes(t))) return;
}

      if (!map.has(gateOut)) map.set(gateOut, []);
      map.get(gateOut).push(row);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, rows]) => ({
        date,
        rows: rows.sort((a, b) => a.fileDate.localeCompare(b.fileDate)),
      }));
  }, [analyticsData, fromdate, todate,searchText]);

  const grandTotal = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const thStyle = {
    textAlign: "left",
    fontWeight: 400,
    textDecoration: "underline",
    fontSize: 11,
    padding: "4px 6px",
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    fontSize: 11,
    padding: "4px 6px",
    whiteSpace: "nowrap",
  };

  return (
    <div ref={filterRef}>
      {/* ---------- Date selector ---------- */}
      <div className="align__date_1">
        <div
          onClick={() => setActiveDates(!activeDates)}
          className="cursor-pointer flex items-center gap-2"
        >
          <h2 className="text-2xl">
            <BsCalendar2Date />
          </h2>
          <span className="bg-white inline-block px-1 rounded">
            {displayDate(fromdate).replace(/\//g, "-")}
          </span>{" "}
          -{" "}
          <span className="bg-white inline-block px-1 rounded">
            {displayDate(todate).replace(/\//g, "-")}
          </span>
        </div>
        <div className="w-[200px]"> </div>

        <input
  type="text"
  value={searchText}
  onChange={(e) => setSearchText(e.target.value)}
  placeholder="Search ..."
  style={{
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 13,
    minWidth: 280,
  }}
/>

        {activeDates && (
          <div className="date__custom">
            <div className="flex justify-between">
              <h1>Recommended</h1>
            </div>
            <button onClick={() => handleDateFilterChange("last3days")}>Last 3 Days</button>
            <button onClick={() => handleDateFilterChange("last7days")}>Last 7 Days</button>
            <button onClick={() => handleDateFilterChange("last30days")}>Last 30 Days</button>
            <h1>Calendar months</h1>
            <button onClick={() => handleDateFilterChange("lastMonth")}>Last Month</button>
            <button onClick={() => handleDateFilterChange("lastQuarter")}>Last Quarter</button>
            <button onClick={() => handleDateFilterChange("lastYear")}>Last Year</button>
            <h1>Custom date</h1>
            <main className="flex mx-1 gap-3">
              <div>
                <p>Start Date :</p>
                <input
                  type="date"
                  value={fromdate}
                  max={todate}
                  onChange={(e) => e.target.value && setFromdate(e.target.value)}
                />
              </div>
              <div>
                <p>End Date :</p>
                <input
                  type="date"
                  value={todate}
                  min={fromdate}
                  onChange={(e) => e.target.value && setTodate(e.target.value)}
                />
              </div>
            </main>
          </div>
        )}
      </div>

      {/* ---------- Report ---------- */}
      <section className="mt-5 px-4">
        {isLoading && <p className="text-sm">Loading...</p>}
        {error && !isLoading && <p className="text-sm text-red-600">{error}</p>}
        {invalidRange && !isLoading && (
          <p className="text-sm text-red-600">Start date cannot be after end date.</p>
        )}
        {!isLoading && !error && !invalidRange && groups.length === 0 && (
          <p className="text-sm">No containers found for the selected dates.</p>
        )}

        {!isLoading &&
          !error &&
          groups.map(({ date, rows }) => (
            <div key={date} style={{ marginBottom: 32 }}>
              <h2
                style={{
                  color: "red",
                  fontWeight: 700,
                  fontSize: 20,
                  marginBottom: 8,
                }}
              >
                {displayDate(date)}
              </h2>

              <div className="overflow-x-auto">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>fileDate</th>
                      <th style={thStyle}>fileNo</th>
                      <th style={thStyle}>client/OWNER</th>
                      <th style={thStyle}>BOOKINGNO</th>
                      <th style={thStyle}>portOfLoading</th>
                      <th style={thStyle}>LoadingDate</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Consignee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.fileNo}-${i}`}>
                        <td style={tdStyle}>{displayDate(r.fileDate)}</td>
                        <td style={tdStyle}>{r.fileNo}</td>
                        <td style={tdStyle}>{r.client}</td>
                        <td style={tdStyle}>{r.bookingNo}</td>
                        <td style={tdStyle}>{r.portOfLoading}</td>
                        <td style={tdStyle}>{displayDate(r.gateOut)}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {formatVgm(r.Consignee)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Per-date count */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 24,
                  marginTop: 16,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  CONTAINER Consignee COUNT:
                </span>
                <span
                  style={{
                    minWidth: 120,
                    textAlign: "right",
                    fontWeight: 700,
                    fontSize: 16,
                    borderTop: "1px solid #000",
                    borderBottom: "3px double #000",
                    padding: "2px 0",
                  }}
                >
                  {rows.length}
                </span>
              </div>
            </div>
          ))}

        {!isLoading && !error && groups.length > 1 && (
          <p style={{ fontWeight: 700, fontSize: 14 }}>
            TOTAL FOR SELECTED DATES: {grandTotal}
          </p>
        )}
      </section>
    </div>
  );
};

export default CfsBooking;
